import { authApiConfig, isWafConfigured } from "config/authApi";

const JSAPI_SCRIPT_ID = "aws-waf-integration-script";
const CHALLENGE_SCRIPT_ID = "aws-waf-challenge-script";
let scriptLoadPromise = null;

const getIntegrationScriptUrl = () => {
  const base = authApiConfig.wafIntegrationUrl.replace(/\/$/, "");
  return `${base}/jsapi.js`;
};

const waitForGlobal = (name, timeoutMs = 10000) =>
  new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window[name]) {
      resolve(window[name]);
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window[name]) {
        window.clearInterval(timer);
        resolve(window[name]);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        reject(new Error(`Timed out waiting for window.${name}`));
      }
    }, 50);
  });

const loadScript = (id, src) =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.getAttribute("data-loaded") === "true") {
        resolve(true);
        return;
      }

      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error(`Failed to load script: ${src}`)),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.defer = true;
    script.onload = () => {
      script.setAttribute("data-loaded", "true");
      resolve(true);
    };
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });

const extractTokenBaseUrl = (jsapiSource) => {
  const match = jsapiSource.match(
    /https:\/\/[^"'`\s]+token\.awswaf\.com\/[^"'`\s]+/
  );
  return match ? match[0].replace(/\/$/, "") : null;
};

const loadChallengeScript = async () => {
  if (window.AwsWafIntegration && window.ChallengeScript) {
    return true;
  }

  const jsapiSource = await fetch(getIntegrationScriptUrl()).then((response) =>
    response.text()
  );
  const tokenBaseUrl = extractTokenBaseUrl(jsapiSource);

  if (!tokenBaseUrl) {
    throw new Error("Could not determine AWS WAF challenge script URL");
  }

  await loadScript(CHALLENGE_SCRIPT_ID, `${tokenBaseUrl}/challenge.js`);
  await waitForGlobal("ChallengeScript");
  await waitForGlobal("AwsWafIntegration");
  return true;
};

export const loadAwsWafScript = () => {
  if (!isWafConfigured()) {
    return Promise.resolve(false);
  }

  if (window.AwsWafCaptcha && window.AwsWafIntegration) {
    return Promise.resolve(true);
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = (async () => {
    await loadScript(JSAPI_SCRIPT_ID, getIntegrationScriptUrl());
    await waitForGlobal("AwsWafCaptcha");
    await loadChallengeScript();
    return true;
  })().catch((error) => {
    scriptLoadPromise = null;
    throw error;
  });

  return scriptLoadPromise;
};

export const ensureAwsWafReady = async ({ requireIntegration = false } = {}) => {
  const loaded = await loadAwsWafScript();
  if (!loaded) {
    return false;
  }

  if (!window.AwsWafCaptcha?.renderCaptcha) {
    throw new Error("AWS WAF CAPTCHA API is not available");
  }

  if (requireIntegration && !window.AwsWafIntegration) {
    throw new Error("AWS WAF integration is not available");
  }

  return true;
};

export const renderAwsWafCaptcha = async (container, { onSuccess, onError }) => {
  await ensureAwsWafReady();

  container.innerHTML = "";

  window.AwsWafCaptcha.renderCaptcha(container, {
    apiKey: authApiConfig.wafApiKey,
    onSuccess: (wafToken) => {
      if (onSuccess) {
        onSuccess(wafToken);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
    dynamicWidth: true,
  });
};

export const wafFetch = async (url, options = {}) => {
  if (!isWafConfigured()) {
    return fetch(url, options);
  }

  await ensureAwsWafReady({ requireIntegration: true });

  if (window.AwsWafIntegration?.fetch) {
    return window.AwsWafIntegration.fetch(url, options);
  }

  const token = await window.AwsWafIntegration.getToken();
  const headers = new Headers(options.headers || {});
  if (token) {
    headers.set("x-aws-waf-token", token);
  }

  return fetch(url, {
    ...options,
    headers,
    credentials: options.credentials || "include",
  });
};
